import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { cn } from '../../utils/cn'
import { deriveOnlineStoreDisplay } from '../../lib/onlineStore'
import type { StorefrontSettings } from '../../types'

/**
 * Top-bar online-store status light. Visible to owner, admin and manager (so staff
 * can nudge the owner to renew) — clicking takes them to the /online-store details
 * page, where only the owner sees the pay/activate/toggle controls.
 *
 * green "Store online" · red "Store offline" · amber trial · red+blinking last 5 days.
 * "Online" is EFFECTIVE: the add-on must be live AND the owner's storefront toggle on.
 */
export function OnlineStorePill() {
  const { tenant, role, onlineStore } = useAuthStore()
  const [, force] = useState(0)
  const navigate = useNavigate()

  // Re-render each second so the last-5-days state flips live without a reload.
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const canView = role === 'owner' || role === 'admin' || role === 'manager'
  if (!tenant || !canView || !onlineStore) return null

  const os = onlineStore
  const sf: StorefrontSettings = tenant.storefront_settings ?? {}
  const d = deriveOnlineStoreDisplay(os, sf.commerce_enabled)

  // Never activated: only the owner sees the "set up" prompt (and only if the add-on is
  // globally enabled). Staff have nothing to nudge yet, so show nothing.
  if (!d.configured) {
    if (role !== 'owner' || !os.enabled) return null
  }

  const daysSuffix = d.daysLeft !== null && d.configured ? ` · ${d.daysLeft}d` : ''

  return (
    <button
      onClick={() => navigate('/online-store')}
      title="Online store"
      className={cn(
        'hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors',
        d.blink && 'animate-pulse',
        d.tone === 'green'
          ? 'bg-sage-100 text-sage-700 hover:bg-sage-200'
          : d.tone === 'red'
          ? 'bg-red-100 text-red-700 hover:bg-red-200'
          : 'bg-amber-100 text-amber-700 hover:bg-amber-200',
      )}
    >
      <span
        className={cn(
          'h-2 w-2 flex-none rounded-full',
          d.tone === 'green' ? 'bg-sage-500' : d.tone === 'red' ? 'bg-red-500' : 'bg-amber-500',
        )}
      />
      {d.label}{daysSuffix}
    </button>
  )
}
